import "./ClientPersonalDetailsComponent.scss";
import * as Yup from "yup";
import {IClientPersonalDetails} from "../../../../shared/models/client.model";
import {useCallback, useEffect, useRef, useState} from "react";
import _ from "lodash";
import {Field, FieldProps, Form, Formik, FormikHelpers, FormikProps, FormikValues} from "formik";
import {CommonService} from "../../../../shared/services";
import {IAPIResponseType} from "../../../../shared/models/api.model";
import {ImageConfig, Misc} from "../../../../constants";
import ButtonComponent from "../../../../shared/components/button/ButtonComponent";
import FormikInputComponent from "../../../../shared/components/form-controls/formik-input/FormikInputComponent";
import FormikDatePickerComponent
    from "../../../../shared/components/form-controls/formik-date-picker/FormikDatePickerComponent";
import FormikSelectDropdownComponent
    from "../../../../shared/components/form-controls/formik-select-dropdown/FormikSelectDropdownComponent";
import {useDispatch, useSelector} from "react-redux";
import {IRootReducerState} from "../../../../store/reducers";
import {setFormChangesData} from "../../../../store/actions/form-state.action";
import moment from "moment";
import FormikSsnInputComponent
    from "../../../../shared/components/form-controls/formik-ssn-input/FormikSsnInputComponent";


interface ClientPersonalDetailsComponentProps {
    onPrevious?: () => void;
    onNext: () => void;
    appointmentId: any;
}

const ClientBasicDetailsFormValidationSchema = Yup.object({
    first_name: Yup.string()
        .required('First Name is required')
        .min(1, 'First Name must be at least 1 character')
        .max(100, 'First name cannot be greater than 100 characters')
    ,
    last_name: Yup.string()
        .required('Last Name is required')
        .min(1, 'Last Name must be at least 1 character')
        .max(100, 'Last name cannot be greater than 100 characters'),
    dob: Yup.mixed()
        .required('Date of Birth is required'),
    gender: Yup.mixed()
        .required('Gender is required'),
    nick_name: Yup.string()
        .max(100, 'Nickname cannot be more than 100 characters'),
    ssn: Yup.string()
        .min(9, 'Enter valid SSN Number')
        .max(9, 'SSN cannot be more than 9-digits')
});

const ClientBasicDetailsFormInitialValues: IClientPersonalDetails = {
    first_name: "",
    last_name: "",
    dob: "",
    gender: "",
    nick_name: "",
    ssn: ""
}

const ClientPersonalDetailsComponent = (props: ClientPersonalDetailsComponentProps) => {
        const {onNext, appointmentId} = props;
        const {currentUser} = useSelector((state: IRootReducerState) => state.account);
        const [clientBasicDetailsFormInitialValues, setClientBasicDetailsFormInitialValues] = useState<IClientPersonalDetails>(_.cloneDeep(ClientBasicDetailsFormInitialValues));
        const formikRef = useRef<FormikProps<FormikValues>>(null);
        const {
            genderList,
            isGenderListLoaded,
            isGenderListLoading
        } = useSelector((state: IRootReducerState) => state.staticData);
        const {
            clientBasicDetails,
            isClientBasicDetailsLoaded,
        } = useSelector((state: IRootReducerState) => state.client);

        const dispatch = useDispatch();

        useEffect(() => {
            if (isClientBasicDetailsLoaded) {
                // Clone the clientBasicDetails object to avoid direct mutation
                const modifiedClientBasicDetails = _.cloneDeep(clientBasicDetails);

                // Check and format the dob field
                if (modifiedClientBasicDetails.dob) {
                    const dob = moment(modifiedClientBasicDetails.dob);
                    if (dob.isValid()) {
                        // Format the dob to the desired format
                        modifiedClientBasicDetails.dob = dob;
                    } else {
                        console.error('Invalid dob format:', modifiedClientBasicDetails.dob);
                    }
                }

                // Set the modified object as the initial values for the form
                setClientBasicDetailsFormInitialValues(modifiedClientBasicDetails);
            }
        }, [clientBasicDetails, isClientBasicDetailsLoaded]);

        const onSubmit = useCallback((values: any, {setSubmitting, setErrors}: FormikHelpers<any>) => {
            const modifiedValues = {
                ...values,
                appointment_id: appointmentId ? appointmentId : currentUser?.appointment_id
            };
            // Check and format the dob field
            if (modifiedValues.dob) {
                const dob = moment(modifiedValues.dob);
                if (dob.isValid()) {
                    modifiedValues.dob = dob.format('YYYY-MM-DD');
                } else {
                    console.error('Invalid dob format:', modifiedValues.dob);
                    setErrors({dob: 'Invalid date of birth format'});
                    setSubmitting(false);
                    return;
                }
            }
            setSubmitting(true);
            CommonService._client.ClientBasicDetailsSaveAPICall(modifiedValues)
                .then((response: IAPIResponseType<any>) => {
                    CommonService._alert.showToast(response[Misc.API_RESPONSE_MESSAGE_KEY], "success");
                    setSubmitting(false);
                    onNext();
                    dispatch(setFormChangesData(false));
                })
                .catch((error: any) => {
                    CommonService.handleErrors(setErrors, error);
                    setSubmitting(false);
                })
        }, [onNext, currentUser?.appointment_id, dispatch, appointmentId]);

        return (
            <div className={'client-personal-details-component'}>
                <Formik
                    validationSchema={ClientBasicDetailsFormValidationSchema}
                    initialValues={clientBasicDetailsFormInitialValues}
                    validateOnChange={false}
                    validateOnBlur={true}
                    enableReinitialize={true}
                    validateOnMount={true}
                    innerRef={formikRef}
                    onSubmit={onSubmit}
                >
                    {({values, validateForm, isValid, isSubmitting, dirty}) => {
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        useEffect(() => {
                            validateForm();
                            if (dirty) {
                                dispatch(setFormChangesData(true));
                            }
                        }, [validateForm, values, dirty]);
                        return (
                            <Form className="t-form" noValidate={true}>
                                <div className="t-form-controls">
                                    <div className="ts-row">
                                        <div className="ts-col-lg-6 ts-col-md-6 ts-col-sm-12">
                                            <Field name={'first_name'} className="t-form-coFntrol">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            id={'first_name_input'}
                                                            label={'First Name'}
                                                            placeholder={'Enter First Name'}
                                                            required={true}
                                                            formikField={field}
                                                            fullWidth={true}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                        <div className="ts-col-lg-6 ts-col-md-6 ts-col-sm-12">
                                            <Field name={'last_name'} className="t-form-control">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            id={'last_name_input'}
                                                            label={'Last Name'}
                                                            placeholder={'Enter Last Name'}
                                                            required={true}
                                                            formikField={field}
                                                            fullWidth={true}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    </div>
                                    <div className="ts-row">
                                        <div className="ts-col-lg-6 ts-col-md-6 ts-col-sm-12">
                                            <Field name={'nick_name'} className="t-form-control">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            id={'nick_name_input'}
                                                            label={'Nickname/Preferred Name'}
                                                            placeholder={'Enter Nickname/Preferred Name'}
                                                            formikField={field}
                                                            fullWidth={true}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                        <div className="ts-col-lg-6 ts-col-md-6 ts-col-sm-12">
                                            <Field name={'dob'} className="t-form-control">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikDatePickerComponent
                                                            id={'dob_input'}
                                                            label={'Date of Birth'}
                                                            placeholder={'Select Date of Birth'}
                                                            required={true}
                                                            formikField={field}
                                                            fullWidth={true}
                                                            // clearable={true}
                                                            maxDate={moment()}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    </div>
                                    <div className="ts-row">
                                        <div className="ts-col-lg-6 ts-col-md-6 ts-col-sm-12">
                                            <Field name={'gender'} className="t-form-control">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikSelectDropdownComponent
                                                            id={'gender_dd'}
                                                            label={'Gender'}
                                                            options={genderList}
                                                            menuPlacement={"top"}
                                                            isDataLoading={isGenderListLoading}
                                                            isDataLoaded={isGenderListLoaded}
                                                            placeholder={'Select Gender'}
                                                            required={true}
                                                            isClearable={false}
                                                            formikField={field}
                                                            fullWidth={true}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                        <div className="ts-col-lg-6 ts-col-md-6 ts-col-sm-12">
                                            <Field name={'ssn'} className="t-form-control">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikSsnInputComponent
                                                            id={'ssn_input'}
                                                            label={'SSN (Social Security Number)'}
                                                            placeholder={'Enter SSN Number'}
                                                            required={false}
                                                            formikField={field}
                                                            fullWidth={true}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    </div>
                                    <div className="t-form-actions">
                                        <ButtonComponent
                                            id={'next_btn'}
                                            size={'large'}
                                            type={"submit"}
                                            suffixIcon={<ImageConfig.ArrowRightCircle/>}
                                            disabled={!isValid || isSubmitting}
                                            isLoading={isSubmitting}
                                        >
                                            Next
                                        </ButtonComponent>
                                    </div>
                                </div>
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        )
            ;

    }
;

export default ClientPersonalDetailsComponent;
